import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useCookies } from 'vue3-cookies';
import useAxios from '@/libs/axios';
import eventBus from '@/eventBus/eventBus';
import mixpanelStore from '@/store/mixpanel/mixpanel';
import { hasActiveSubscriptionUntil } from '@/utils/hasActiveSubscription';

export default defineStore('authStore', () => {
  const axios = useAxios();

  const initialValues = {
    uuid: '',
    hasAccessToken: false,
    language: '',
    name: '',
    profileImageUrl: '',
    status: '',
    timezone: '',
    ipAddress: '',
    stripeCustomerId: '',
    storeMode: 'customer', // customer | merchant
    affiliateCode: '',
    activeSubscriptionUntil: null as string | null,
  };

  /*
    State
  */
  const session = ref(initialValues);

  /*
    Getters
  */
  const getToken = (): string => {
    const { cookies } = useCookies();
    return cookies.get('access_token');
  };
  const isAuthenticated = (): boolean => (
    !!session.value?.hasAccessToken
    && !!getToken()
  );
  const isStoreModeCustomer = (): boolean => (
    !!session.value?.hasAccessToken
    && !!getToken() && session.value?.storeMode === 'customer'
  );
  const isStoreModeMerchant = (): boolean => (
    !!session.value?.hasAccessToken
    && !!getToken() && session.value?.storeMode === 'merchant'
  );
  const mustChangePassword = (): boolean => ['REQUEST_PASSWORD_UPDATE_EMAIL', 'REQUEST_PASSWORD_UPDATE_MOBILE'].includes(session.value?.status);
  // const getUserFirstName = (): string => session.value?.name.split(' ')[0];

  const isLiveLinkAdmin = (): boolean => {
    const leUuid = '20613d5e-a583-4d3d-8bfb-6142a3646c1f';
    const samUuid = '65bd1960-3251-4adf-9b44-492e31f77683';
    const ruanUuid = '797ee1f4-6b2d-47cf-9718-49e33928b93d';
    return [leUuid, samUuid, ruanUuid].includes(session.value.uuid);
  };

  const hasActiveSubscription = (): boolean => (
    hasActiveSubscriptionUntil(session.value.activeSubscriptionUntil)
  );

  /*
    Actions
  */

  // Reset the whole store and remove it from localStorage after user
  // logout. Two things this MUST do that the old version did not:
  //   1. Spread initialValues into a NEW object. Vue refs short-circuit
  //      when the new value is `Object.is`-equal to the current one, and
  //      `session.value = initialValues` hits that path (initialValues
  //      is the same object we assigned at line 29). Result: reactivity
  //      never fires, the deep watcher never re-persists, and any prior
  //      mutation to `initialValues` (e.g. `session.value.hasAccessToken
  //      = true` at login) survives.
  //   2. Remove the access_token cookie. `authDoLogout` only calls the
  //      backend + emits `event:logout`; without the cookie remove here,
  //      `isAuthenticated()` (which checks the cookie) still returns
  //      true after logout, and the router-guard admits back into
  //      protected routes.
  const resetStore = () => {
    const { cookies } = useCookies();
    localStorage.removeItem('authStore');
    cookies.remove('access_token');
    session.value = { ...initialValues };
  };

  // This event is used to force logout in all tabs
  // when user logout in one tab
  // This is necessary because localStorage is shared
  // between tabs
  const emitLogout = async () => {
    await eventBus.emit('event:logout');
  };

  const toggleStoreMode = async () => {
    if (!session.value.storeMode || session.value.storeMode === 'customer') {
      session.value.storeMode = 'merchant';
    } else {
      session.value.storeMode = 'customer';
    }
  };

  const getStoreMode = async () => session.value.storeMode;

  // This method is used to force logout in all tabs
  const authDoForceLogout = async () => {
    emitLogout();
  };

  const authDoLogin = async (params: {
    loginMethod: 'mobile' | 'document',
    mobile?: string,
    document?: string,
    documentType?: string,
    password: string,
    token?: string,
  }) => {
    const { cookies } = useCookies();

    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload: Record<string, unknown> = {
      login_method: params.loginMethod,
      password: params.password,
    };

    if (params.token) {
      payload.token = params.token;
    }

    if (params.loginMethod === 'mobile') {
      payload.mobile = params.mobile;
    } else {
      payload.document = params.document;
      payload.documentType = params.documentType;
    }

    return axios.post(
      '/session/login',
      payload,
      config,
    ).then((response) => {
      mixpanelStore.userIdentify(response.data?.uuid);

      cookies.set('access_token', response.data?.token, '1d');
      session.value.hasAccessToken = true;
      session.value.uuid = response.data?.uuid;
      session.value.language = response.data?.language;
      session.value.name = response.data?.name;
      session.value.profileImageUrl = response.data?.profile_image_url;
      session.value.status = response.data?.status;
      session.value.timezone = response.data?.timezone;
      session.value.ipAddress = response.data?.ip_address;
      session.value.stripeCustomerId = response.data?.stripe_customer_id;
      session.value.storeMode = 'customer';
      session.value.affiliateCode = response.data?.affiliate_code;
      session.value.activeSubscriptionUntil = response.data?.active_subscription_until || null;
      return response;
    }).catch((error) => {
      if (error.response?.data?.error !== undefined) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  // This method is used to refresh the access token
  // when it expires
  const authDoLoginRefresh = async () => {
    const { cookies } = useCookies();

    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {};

    return axios.post(
      '/session/login/refresh',
      payload,
      config,
    ).then((response) => {
      cookies.set('access_token', response.data?.token, '1d');
      session.value.hasAccessToken = true;
      session.value.language = response.data?.language;
      session.value.name = response.data?.name;
      session.value.profileImageUrl = response.data?.profile_image_url;
      session.value.status = response.data?.status;
      session.value.timezone = response.data?.timezone;
      session.value.ipAddress = response.data?.ip_address;
      session.value.stripeCustomerId = response.data?.stripe_customer_id;
      session.value.activeSubscriptionUntil = response.data?.active_subscription_until || null;
      return response;
    }).catch((error) => {
      session.value.hasAccessToken = false;
      session.value.language = 'pt-BR';
      session.value.name = '';
      session.value.profileImageUrl = '';
      session.value.status = '';
      session.value.timezone = '';
      session.value.ipAddress = '';
      session.value.stripeCustomerId = '';
      cookies.remove('access_token');

      if (error.response?.data?.error !== undefined) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  // This method is used to logout user
  // It will remove the access token from cookies
  // and reset the store
  const authDoLogout = async () => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {};

    return axios.post(
      '/session/logout',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch(
      (error) => error,
    ).finally(() => {
      emitLogout();
    });
  };

  // This method is used to create a new user
  // It will send an email to the user with
  // a first access password
  const authDoSignup = async (
    language: string,
    timezone: string,
    fullName: string,
    document: string,
    documentType: string,
    email: string,
    mobile: string,
    affiliateCode: string,
    activeSubscriptionUntil: string | null,
  ) => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {
      language,
      timezone,
      full_name: fullName,
      document,
      document_type: documentType,
      email,
      mobile_number: mobile,
      affiliate_code: affiliateCode,
      active_subscription: activeSubscriptionUntil,
    };

    return axios.post('/session/signup', payload, config).then(
      (response) => {
        mixpanelStore.userIdentify(response.data?.uuid);
        return response.data.message;
      },
    ).catch((error) => {
      if (error.response?.data?.error !== undefined) {
        // Carry the backend's stable `code` onto the Error. Callers must
        // branch on it rather than the message, which is translated prose:
        // `already_registered_mobile` drives the "you already have an
        // account" prompt instead of a generic failure banner.
        const err = new Error(error.response.data.error) as Error & { code?: string };
        if (error.response.data.code) err.code = error.response.data.code;
        throw err;
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  // This method is used to recover user password
  // It will send an email to the user with
  // a temporary password
  const authDoPasswordRecover = async (
    recoverMethod: string,
    documentType: string,
    document: string,
    mobile?: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {
      recover_method: recoverMethod,
      document_type: documentType,
      document,
      mobile_number: mobile,
    };

    return axios.post(
      '/session/password/recover',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      if (error.response?.data?.error !== undefined) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  // This method is used to update user password
  // It will update the user password as long as
  // the user is in a mustChangePassword status
  const authDoPasswordUpdate = async (
    password: string,
    passwordConfirmation: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {
      password,
      password_confirmation: passwordConfirmation,
      validate_current_password: false,
    };

    return axios.post(
      '/session/password/update',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      if (error.response?.data?.error !== undefined) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  const authDoCurrentPasswordUpdate = async (
    password: string,
    passwordConfirmation: string,
    currentPassword: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'en-US',
      },
    };

    const payload = {
      password,
      password_confirmation: passwordConfirmation,
      current_password: currentPassword,
      validate_current_password: true,
    };

    return axios.post(
      '/session/password/update',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      if (error.response?.data?.error !== undefined) {
        throw new Error(error.response.data.error);
      } else if (error.response?.data?.errors !== undefined) {
        throw new Error(error.response.data.errors);
      } else {
        throw error;
      }
    });
  };

  /*
    Config
  */

  // Listen to logout event
  // This event is used to clear the store
  eventBus.on('event:logout', resetStore);

  // Load store from localStorage. Wrapped in try/catch because any
  // corrupted / tampered value would throw at module-init and lock the
  // user out of the whole SPA (browser extension munging, quota trims,
  // a schema roll-forward). Fail-safe: drop the bad blob and boot fresh.
  try {
    const raw = localStorage.getItem('authStore');
    if (raw) {
      session.value = JSON.parse(raw);
    }
  } catch (err) {
    console.warn('[authStore] corrupted localStorage, resetting', err);
    localStorage.removeItem('authStore');
    session.value = { ...initialValues };
  }

  watch(session, (sessionVal) => {
    localStorage.setItem('authStore', JSON.stringify(sessionVal) || JSON.stringify(initialValues));
  }, {
    deep: true,
  });

  return {
    session,
    isLiveLinkAdmin,
    hasActiveSubscription,
    resetStore,
    isAuthenticated,
    isStoreModeCustomer,
    isStoreModeMerchant,
    toggleStoreMode,
    getStoreMode,
    mustChangePassword,
    authDoForceLogout,
    authDoLogin,
    authDoLoginRefresh,
    authDoLogout,
    authDoSignup,
    authDoPasswordRecover,
    authDoPasswordUpdate,
    authDoCurrentPasswordUpdate,
  };
});
